import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Buffer } from "buffer";
import { AxiosError } from "axios";

import { IRecoverRequest } from "../../../interfaces";
import { formatCpf, formatPhone } from "../../../utils/Masks";
import { urlHttpGov } from "../../../services/urls";
import { useRecoverRequestsContext } from "../../../contexts/RecoverRequestsContext";
import { EmitError } from "../../../utils/EmitError";
import { EmitSuccessAlert } from "../../../utils/EmitSuccessAlert";
import privateHttpClient from "../../../services/http/privateHttpClient";
import AppAuthService from "../../../services/http/services/AppAuthService";

export default function useViewRecoverRequest() {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [recoverRequest, setRecoverRequest] = useState<IRecoverRequest | undefined>();
    const [images, setImages] = useState<Array<string>>([]);
    const [observation, setObservation] = useState<string>("");
    const [observationError, setObservationError] = useState<string | null>(null);

    const [isConfirmApproveRequestModalVisible, setIsConfirmApproveRequestModalVisible] = useState<boolean>(false);
    const [isConfirmRefuseRequestModalVisible, setIsConfirmRefuseRequestModalVisible] = useState<boolean>(false);

    const { recoverRequests, loadRecoverRequests } = useRecoverRequestsContext();

    const { id } = useParams();
    const navigate = useNavigate();

    const isPending = recoverRequest?.status === "Em Análise" || !recoverRequest?.status;
    const isApproved = recoverRequest?.status === "Aprovado!" || recoverRequest?.status === "Aprovado";
    const isRefused = recoverRequest?.status === "Recusado!" || recoverRequest?.status === "Recusado";

    const handleApproveRequest = useCallback(() => {
        setObservationError(null);
        setIsConfirmApproveRequestModalVisible(true);
    }, []);

    const handleRefuseRequest = useCallback(() => {
        if (!observation || !observation.trim()) {
            setObservationError("Ao recusar uma solicitação, a observação/motivo é obrigatória!");
            return;
        }
        setObservationError(null);
        setIsConfirmRefuseRequestModalVisible(true);
    }, [observation]);

    const handleConfirmApproveRequest = useCallback(async () => {
        try {
            setIsSubmiting(true);
            if (!recoverRequest) return;

            await AppAuthService.approveRecoverRequest(recoverRequest, observation);

            EmitSuccessAlert("Solicitação aprovada com sucesso! O número de telefone foi atualizado.");
            navigate("/recuperacao-acesso");
        } catch (err) {
            EmitError(err, true, "viewRecoverRequest_approve");
        } finally {
            setIsSubmiting(false);
            setIsConfirmApproveRequestModalVisible(false);
        }
    }, [recoverRequest, observation, navigate]);

    const handleConfirmRefuseRequest = useCallback(async () => {
        try {
            setIsSubmiting(true);
            if (!recoverRequest) return;

            if (!observation || !observation.trim()) {
                setObservationError("Ao recusar uma solicitação, a observação/motivo é obrigatória!");
                setIsConfirmRefuseRequestModalVisible(false);
                return;
            }

            await AppAuthService.refuseRecoverRequest(recoverRequest, observation);

            EmitSuccessAlert("Solicitação recusada com sucesso!");
            navigate("/recuperacao-acesso");
        } catch (err) {
            EmitError(err, true, "viewRecoverRequest_refuse");
        } finally {
            setIsSubmiting(false);
            setIsConfirmRefuseRequestModalVisible(false);
        }
    }, [recoverRequest, observation, navigate]);

    const handleCancel = useCallback(() => {
        setIsConfirmApproveRequestModalVisible(false);
        setIsConfirmRefuseRequestModalVisible(false);
    }, []);

    const loadBase64Image = useCallback(async (url: string, signal: AbortSignal): Promise<string | null> => {
        try {
            if (!window.Buffer) {
                window.Buffer = Buffer;
            }

            const response = await privateHttpClient.get(url, { responseType: "arraybuffer", signal });
            const data = `data:${response.headers["content-type"]};base64,${Buffer.from(response.data, "binary").toString("base64")}`;
            return data;
        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return null;
            }
            EmitError(err, true, "viewRecoverAccess_image_load");
            return null;
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadRequestAndImages() {
            setIsLoading(true);

            if (recoverRequests.length === 0) {
                await loadRecoverRequests(controller);
            }

            const request = recoverRequests.find((r) => r.id === Number(id));

            if (!request && recoverRequests.length > 0) {
                EmitError("Solicitação não encontrada.", true, "viewRecoverRequest_not_found");
                navigate("/recuperacao-acesso");
                setIsLoading(false);
                return;
            }

            if (request) {
                setRecoverRequest(request);
                setObservation(request.observation || "");

                // Load all images in parallel
                if (request.images && request.images.length > 0) {
                    setIsLoadingImages(true);
                    const imagesBaseUrl = `${urlHttpGov}/panel/auth_app/recover_documents`;
                    const promises = request.images.map((imgObj) =>
                        loadBase64Image(`${imagesBaseUrl}/${imgObj.img}`, controller.signal)
                    );

                    const loadedImages = await Promise.all(promises);
                    setImages(loadedImages.filter(Boolean) as string[]);
                    setIsLoadingImages(false);
                } else {
                    setImages([]);
                }
            }

            setIsLoading(false);
        }

        loadRequestAndImages();

        return () => {
            controller.abort();
        };
    }, [id, recoverRequests, loadRecoverRequests, loadBase64Image, navigate]);

    const handleObservationChange = useCallback((value: string) => {
        setObservation(value);
        if (value.trim()) {
            setObservationError(null);
        }
    }, []);

    return {
        isLoading,
        isLoadingImages,
        isSubmiting,
        recoverRequest,
        images,
        observation,
        observationError,
        isPending,
        isApproved,
        isRefused,
        handleObservationChange,
        handleApproveRequest,
        handleRefuseRequest,
        isConfirmApproveRequestModalVisible,
        isConfirmRefuseRequestModalVisible,
        handleConfirmApproveRequest,
        handleConfirmRefuseRequest,
        handleCancel,
        formattedData: {
            userName: recoverRequest?.app_user?.name || "--",
            userCpf: recoverRequest?.app_user?.cpf ? formatCpf(recoverRequest.app_user.cpf) : "--",
            existingPhone: recoverRequest?.existing_phone ? formatPhone(recoverRequest.existing_phone) : "--",
            newPhone: recoverRequest?.new_phone ? formatPhone(recoverRequest.new_phone) : "--",
            createdAt: recoverRequest?.created_at || "",
            dateResponse: recoverRequest?.date_response || "",
            operatorName: recoverRequest?.panel_user?.name || "",
        },

    };
}